// Паттерн «живого» слоя: периодический перезапрос тайлов у слоёв, данные которых меняются
// в базе между действиями пользователя (вероятные призраки, Таба/Форкаст ребаланса).
//
// Слой объявляет в constants/layers.js поле liveRefresh — true или объект с интервалами:
//     liveRefresh: { activeMs: 3000, idleMs: 10000, stopAfterMs: 60000 }
// activeMs   — период обновления, пока пользователь возит карту (пан/зум);
// idleMs     — период, когда карта стоит, но пользователь ещё за картой;
// stopAfterMs — через сколько бездействия обновления прекращаются до следующего действия.
//
// Обновление идёт подменой источника (A/B swap), а не setTiles: setTiles чистит кэш тайлов,
// и слой на 200–400 мс становится пустым — на периодическом обновлении это мигание.
// Здесь рядом заводится второй источник с тем же URL плюс cache-busting параметр, он
// прогревается скрытым слоем нулевого радиуса, и, только когда тайлы пришли, слои
// переключаются на него, а старый источник удаляется. Пользователь видит подмену как
// обычную перерисовку.

export const LIVE_REFRESH_DEFAULTS = {
    activeMs: 3000,
    idleMs: 10000,
    stopAfterMs: 60000,
};

// Суффикс запасного источника; чередуется с основным при каждом обновлении
const ALT_SUFFIX = '__live_b';
// Служебный слой, которым прогревается новый источник: без слоя mapbox тайлы не запрашивает
const WARMUP_SUFFIX = '__live_warmup';
// Защита от зависшего запроса: если тайлы не пришли за это время, свопа не будет
const SWAP_TIMEOUT_MS = 15000;
const baseSourceId = sourceId => (sourceId.endsWith(ALT_SUFFIX)
    ? sourceId.slice(0, -ALT_SUFFIX.length)
    : sourceId);
const otherSourceId = sourceId => (sourceId.endsWith(ALT_SUFFIX)
    ? baseSourceId(sourceId)
    : sourceId + ALT_SUFFIX);
// Эпоха обновления по имени MVT-источника (martin.<name>_mvt): растёт при каждом перезапросе.
// В TileJSON-URL _ts подставляется здесь, а в URL самих тайлов — в transformMapRequests: без этого
// тайлы вернулись бы из HTTP-кэша браузера, и обновление оказалось бы пустой работой.
const epochs = new Map();
// martin.ghost_devices_mvt из https://…/rpc/martin.ghost_devices_mvt.json
const sourceKeyFromUrl = url => {
    const [ path ] = url.split('?');
    return path.split('/').pop().replace(/\.json$/, '');
};

// Текущее значение _ts для URL тайла живого слоя; null — слой не живой, параметр не нужен
export const getLiveTileEpoch = url => {
    for (const [ key, ts ] of epochs) {
        if (url.includes(key)) return ts;
    }
    return null;
};

// URL источника с новым значением _ts: без него ни mapbox, ни браузер за новыми тайлами не пойдут
const bustedUrl = url => {
    const [ base, query = '' ] = url.split('?');
    const params = new URLSearchParams(query);
    const ts = Date.now();
    epochs.set(sourceKeyFromUrl(base), ts);
    params.set('_ts', String(ts));
    return `${base}?${params.toString()}`;
};

export class LiveTileRefresh {
    // map — экземпляр mapbox-gl; onAfterSwap — колбэк после подмены (в Map.jsx — scheduleReorder)
    constructor ({ map, onAfterSwap } = {}) {
        this.map = map;
        this.onAfterSwap = onAfterSwap;
        // sourceId → { activeMs, idleMs, stopAfterMs }; ключ всегда базовый (без ALT_SUFFIX)
        this.layers = new Map();
        // базовый sourceId → фактический (основной или запасной)
        this.currentSourceIds = new Map();
        this.pending = new Set();
        this.lastInteractionAt = Date.now();
        this.isMoving = false;
        this.timer = null;

        this.handleMoveStart = () => { this.isMoving = true; this.notifyInteraction() };
        this.handleMoveEnd = () => { this.isMoving = false; this.notifyInteraction() };
        this.handleInteraction = () => this.notifyInteraction();
        this.handleVisibility = () => {
            if (!document.hidden) this.notifyInteraction();
        };

        if (this.map) {
            this.map.on('movestart', this.handleMoveStart);
            this.map.on('moveend', this.handleMoveEnd);
            this.map.on('mousedown', this.handleInteraction);
            this.map.on('wheel', this.handleInteraction);
        }
        document.addEventListener('visibilitychange', this.handleVisibility);
    }

    // Список живых слоёв текущего состояния карты: [{ sourceId, activeMs, idleMs, stopAfterMs }]
    sync (liveLayers = []) {
        const next = new Map();
        liveLayers.forEach(({ sourceId, ...options }) => {
            if (sourceId) next.set(sourceId, { ...LIVE_REFRESH_DEFAULTS, ...options });
        });
        // Слой выключили — вернуть его источник к исходному id, чтобы следующее включение
        // не наткнулось на запасной источник с чужим именем
        this.layers.forEach((_, sourceId) => {
            if (!next.has(sourceId)) this.restoreSourceId(sourceId);
        });
        this.layers = next;
        if (this.layers.size) {
            this.notifyInteraction();
        } else {
            this.stopTimer();
        }
    }

    // Любое действие пользователя продлевает окно обновлений и перезапускает таймер
    notifyInteraction () {
        this.lastInteractionAt = Date.now();
        this.scheduleTick();
    }

    scheduleTick () {
        this.stopTimer();
        if (!this.map || !this.layers.size || document.hidden) return;
        const delays = [ ...this.layers.values() ].map(o => (this.isMoving ? o.activeMs : o.idleMs));
        const delay = Math.min(...delays);
        this.timer = setTimeout(() => this.tick(), delay);
    }

    tick () {
        this.timer = null;
        if (!this.map || !this.layers.size) return;
        const idleFor = Date.now() - this.lastInteractionAt;
        const stopAfter = Math.max(...[ ...this.layers.values() ].map(o => o.stopAfterMs));
        // Пользователь ушёл — обновления прекращаются до следующего движения карты или клика
        if (idleFor > stopAfter || document.hidden) return;
        this.layers.forEach((_, sourceId) => this.refresh(sourceId));
        this.scheduleTick();
    }

    // Фактический id источника: после нечётного числа обновлений это запасной источник
    resolveSourceId (sourceId) {
        const current = this.currentSourceIds.get(sourceId) || sourceId;
        return this.map && this.map.getSource(current) ? current : sourceId;
    }

    refresh (sourceId) {
        if (!this.map || this.pending.has(sourceId)) return;
        const fromId = this.resolveSourceId(sourceId);
        const style = this.map.getStyle();
        const source = style && style.sources && style.sources[fromId];
        if (!source || !source.url) return;
        const layers = (style.layers || []).filter(layer => layer.source === fromId);
        if (!layers.length) return;

        const toId = otherSourceId(fromId);
        const warmupId = baseSourceId(sourceId) + WARMUP_SUFFIX;
        this.pending.add(sourceId);

        let timeout = null;
        let onData = null;
        const cleanup = () => {
            this.map.off('sourcedata', onData);
            clearTimeout(timeout);
            this.pending.delete(sourceId);
            if (this.map.getLayer(warmupId)) this.map.removeLayer(warmupId);
        };
        onData = e => {
            if (e.sourceId !== toId || !e.isSourceLoaded) return;
            cleanup();
            try {
                // Слои переставляются по одному, каждый — на своё прежнее место в стеке
                const order = this.map.getStyle().layers.map(l => l.id);
                layers.forEach(layer => {
                    const index = order.indexOf(layer.id);
                    const beforeId = order.slice(index + 1).find(id => id !== warmupId && this.map.getLayer(id));
                    this.map.removeLayer(layer.id);
                    this.map.addLayer({ ...layer, source: toId }, beforeId);
                });
                this.map.removeSource(fromId);
                this.currentSourceIds.set(baseSourceId(sourceId), toId);
                if (this.onAfterSwap) this.onAfterSwap();
            } catch (error) {
                console.warn('LiveTileRefresh: не удалось подменить источник', sourceId, error);
            }
        };
        timeout = setTimeout(() => {
            // Тайлы не пришли — откатываемся к работающему источнику, слои не трогаем
            cleanup();
            if (this.map.getSource(toId)) this.map.removeSource(toId);
        }, SWAP_TIMEOUT_MS);

        try {
            const { id, ...sourceParams } = source;
            if (this.map.getSource(toId)) this.map.removeSource(toId);
            this.map.addSource(toId, { ...sourceParams, url: bustedUrl(source.url) });
            this.map.on('sourcedata', onData);
            // Прогрев: источник без слоёв тайлы не грузит, поэтому вешаем невидимый слой
            this.map.addLayer({
                id: warmupId,
                type: 'circle',
                source: toId,
                'source-layer': layers[0]['source-layer'],
                paint: { 'circle-radius': 0, 'circle-opacity': 0 },
            });
        } catch (error) {
            console.warn('LiveTileRefresh: не удалось создать источник', toId, error);
            cleanup();
        }
    }

    // Возврат к исходному id источника при выключении слоя — чтобы initLayer нашёл свой source
    restoreSourceId (sourceId) {
        const current = this.currentSourceIds.get(sourceId);
        this.currentSourceIds.delete(sourceId);
        if (!this.map || !current || current === sourceId) return;
        const style = this.map.getStyle();
        const source = style && style.sources && style.sources[current];
        if (!source) return;
        const layers = (style.layers || []).filter(layer => layer.source === current);
        try {
            layers.forEach(layer => this.map.removeLayer(layer.id));
            this.map.removeSource(current);
            const { id, ...sourceParams } = source;
            this.map.addSource(sourceId, sourceParams);
            layers.forEach(layer => this.map.addLayer({ ...layer, source: sourceId }));
            if (this.onAfterSwap) this.onAfterSwap();
        } catch (error) {
            console.warn('LiveTileRefresh: не удалось вернуть источник', sourceId, error);
        }
    }

    stopTimer () {
        if (this.timer) {
            clearTimeout(this.timer);
            this.timer = null;
        }
    }

    dispose () {
        this.stopTimer();
        this.pending.clear();
        this.layers.clear();
        if (this.map) {
            this.map.off('movestart', this.handleMoveStart);
            this.map.off('moveend', this.handleMoveEnd);
            this.map.off('mousedown', this.handleInteraction);
            this.map.off('wheel', this.handleInteraction);
        }
        document.removeEventListener('visibilitychange', this.handleVisibility);
    }
}
